import { FormCommonProps } from "src/components/common/forms/types.ts";
import { AavoForm } from "src/components/common/forms/AavoForm.tsx";
import { Contact } from "src/api/generated/erp/db/types/tables/contact.ts";
import { FormTextField } from "src/components/common/forms/fields/FormTextField.tsx";
import i18n from "i18next";
import { requireRule } from "src/components/common/forms/validation.ts";
import { ContactConnectedItemType } from "src/api/generated/erp/db/types/enums/contactConnectedItemType.ts";
import { FormCheckbox } from "src/components/common/forms/fields/FormCheckbox.tsx";
import { useTenantCustomizations } from "src/tenantCustomizations/TenantCustomizationsContext.ts";
import { defineAgentUiContextKey } from "src/components/views/ai/assistant/uiContext/agentUiContextKey.ts";
import { getContactEndpoints } from "src/components/views/erp/contacts/contactEndpoints.ts";

export interface ContactFormProps extends FormCommonProps<number> {
	connectedItemType: ContactConnectedItemType;
	connectedItemId: number;
	contact?: Contact;
}

export const ContactForm = ({
	connectedItemType,
	connectedItemId,
	contact,
	onFormEdited,
	onCompleted,
}: ContactFormProps) => {
	const { tenantConfig } = useTenantCustomizations();
	const showContractSignerField =
		connectedItemType === "CUSTOMER" && tenantConfig.erp.customerOfferContractSigningEnabled;
	const showDefaultContactField =
		connectedItemType === "SALES_LEAD" ||
		(connectedItemType === "CUSTOMER" && tenantConfig.erp.customerFormUseDefaultContact);
	const contactEndpoints = getContactEndpoints(connectedItemType, connectedItemId);

	return (
		<AavoForm<Contact, number>
			defaultValues={{
				connectedItemId: connectedItemId,
				connectedItemType: connectedItemType,
				isContractSigner: false,
				isDefaultContact: false,
				...contact,
			}}
			onCompleted={onCompleted}
			onFormEdited={onFormEdited}
			submit={async (values) => {
				if (contact == null) {
					return await contactEndpoints.insert(values);
				} else {
					return await contactEndpoints.update(values);
				}
			}}
			agentContext={{
				key: defineAgentUiContextKey("erp.contacts.contact.form"),
				title: i18n.t("contact"),
				pathSegments: [i18n.t("form")],
				data: {
					connectedItemType,
					connectedItemId,
					contactId: contact?.contactId ?? null,
				},
			}}
			render={({ control }) => (
				<>
					<FormTextField
						control={control}
						name={"name"}
						label={i18n.t("name")}
						rules={connectedItemType === "SALES_LEAD" ? salesLeadContactNameRule : requireRule()}
					/>
					<FormTextField control={control} name={"designation"} label={i18n.t("designation")} />
					<FormTextField control={control} name={"email"} label={i18n.t("email")} />
					<FormTextField control={control} name={"phone"} label={i18n.t("phone")} />
					<FormTextField control={control} name={"note"} label={i18n.t("note")} />
					{showContractSignerField && (
						<FormCheckbox
							control={control}
							name={"isContractSigner"}
							label={i18n.t("is_contract_signer")}
						/>
					)}
					{showDefaultContactField && (
						<FormCheckbox
							control={control}
							name={"isDefaultContact"}
							label={i18n.t("default_contact")}
						/>
					)}
				</>
			)}
		/>
	);
};

/** Sales lead contacts migrated from the former inline lead fields may have only an email or a phone. */
const salesLeadContactNameRule = {
	validate: (name: string, contact: Contact) =>
		[name, contact.email, contact.phone].some((value) => (value ?? "").trim() !== "") ||
		i18n.t("contact_name_email_or_phone_required"),
};
